//@ts-nocheck
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  ApiError,
  DepartmentResponse,
  TeamsState,
  updateTeamsPayload,
} from "./JobApplicationTypes";
import {
  createJobApplicationStepData,
  fetchJobApplicationDetails,
  fetchQuestionListData,
  updateJobApplicationData,
} from "../../services/jobInternalService";

// Update job application
export const updateJobApplication = createAsyncThunk<
  DepartmentResponse,
  { payload: updateTeamsPayload; baseId: number; id: number },
  { rejectValue: string }
>(
  "jobApplication/update",
  async ({ payload, baseId, id }, { rejectWithValue }) => {
    try {
      const response = await updateJobApplicationData(baseId, id, payload);
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(
        error.response?.data?.message || "Failed to update job application"
      );
    }
  }
);

// Fetch job application details
export const fetchJobApplication = createAsyncThunk<
  DepartmentResponse,
  { baseId: number; id: number },
  { rejectValue: string }
>("jobApplication/fetch", async ({ baseId, id }, { rejectWithValue }) => {
  try {
    const response = await fetchJobApplicationDetails(baseId, id);
    return response;
  } catch (err) {
    const error = err as ApiError;
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch job application"
    );
  }
});

// create application question set
export const createJobApplicationQuestionStep = createAsyncThunk<
  TeamsResponse,
  { integrationSteps: createTeamsPayload[]; userId: number },
  { rejectValue: string }
>(
  "base/createJobApplicationQuestionStep",
  async ({ integrationSteps, userId }, { rejectWithValue }) => {
    try {
      const response = await createJobApplicationStepData(
        integrationSteps,
        userId
      );
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(
        error.response?.data?.message || "Failed to create location"
      );
    }
  }
);

export const fetchJobQuestionStepList = createAsyncThunk<
  TeamsResponse,
  { baseId: number; jobId: number },
  { rejectValue: string }
>(
  "base/fetchJobQuestionStepList",
  async ({ baseId, jobId }, { rejectWithValue }) => {
    try {
      if (baseId === undefined) {
        throw new Error("Base ID is undefined");
      }
      const response = await fetchQuestionListData(baseId, jobId);
      return response;
    } catch (err) {
      const error = err as Error;
      return rejectWithValue(error.message || "Failed to fetch base data");
    }
  }
);

// Initial state
const initialState: TeamsState = {
  updateTeamsStatus: "idle",
  updateTeamsError: null,
  updateTeamsData: null,

  fetchTeamsByIdStatus: "idle",
  fetchTeamsByIdError: null,
  fetchTeamsByIdData: null,
  createQuestionSetStatus: "idle",
  createQuestionSetError: null,
  createQuestionSetData: null,
  fetchQuestionSetStatus: "idle",
  fetchQuestionSetError: null,
  fetchQuestionSetData: null,
};

const jobApplicationSlice = createSlice({
  name: "jobApplication",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Handle update job application
    builder
      .addCase(updateJobApplication.pending, (state) => {
        state.updateTeamsStatus = "loading";
        state.updateTeamsError = null;
      })
      .addCase(updateJobApplication.fulfilled, (state, action) => {
        state.updateTeamsStatus = "succeeded";
        state.updateTeamsData = action.payload;
        state.updateTeamsError = null;
      })
      .addCase(updateJobApplication.rejected, (state, action) => {
        state.updateTeamsStatus = "failed";
        state.updateTeamsError =
          action.payload || "Failed to update job application";
      });

    // Handle fetch job application
    builder
      .addCase(fetchJobApplication.pending, (state) => {
        state.fetchTeamsByIdStatus = "loading";
        state.fetchTeamsByIdError = null;
      })
      .addCase(fetchJobApplication.fulfilled, (state, action) => {
        state.fetchTeamsByIdStatus = "succeeded";
        state.fetchTeamsByIdData = action.payload;
        state.fetchTeamsByIdError = null;
      })
      .addCase(fetchJobApplication.rejected, (state, action) => {
        state.fetchTeamsByIdStatus = "failed";
        state.fetchTeamsByIdError =
          action.payload || "Failed to fetch job application";
      })
      .addCase(createJobApplicationQuestionStep.pending, (state) => {
        state.createQuestionSetStatus = "loading";
        state.createQuestionSetError = null;
      })
      .addCase(createJobApplicationQuestionStep.fulfilled, (state, action) => {
        state.createQuestionSetStatus = "succeeded";
        state.createQuestionSetData = action.payload;
        state.createQuestionSetError = null;
      })
      .addCase(createJobApplicationQuestionStep.rejected, (state, action) => {
        state.createQuestionSetStatus = "failed";
        state.createQuestionSetError =
          action.payload || "Failed to create location";
      })
      .addCase(fetchJobQuestionStepList.pending, (state) => {
        state.fetchQuestionSetStatus = "loading";
        state.fetchQuestionSetError = null;
      })
      .addCase(fetchJobQuestionStepList.fulfilled, (state, action) => {
        state.fetchQuestionSetStatus = "succeeded";
        state.fetchQuestionSetData = action.payload;
        state.fetchQuestionSetError = null;
      })
      .addCase(fetchJobQuestionStepList.rejected, (state, action) => {
        state.fetchQuestionSetStatus = "failed";
        state.fetchQuestionSetError =
          action.payload || "Failed to fetch base data";
      });
  },
});

export default jobApplicationSlice.reducer;
